<script setup lang="ts">
import { inject, onMounted, onBeforeUnmount } from 'vue';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useViewSliceStore } from '@/src/store/view-configs/slicing';
import type { LPSAxis } from '@/src/types/lps';
import { useDentalCbctStore } from './cbctStore';
import { useDentalTaskStore } from './taskStore';
import { observeView } from './sceneObservation';
import { useDentalRegistrationStore } from './registrationStore';
const props = defineProps<{ viewId: string; imageId?: string | null; axis: LPSAxis }>();
const view = inject(VtkViewContext)!; const cbct = useDentalCbctStore(); const task = useDentalTaskStore();
const reg = useDentalRegistrationStore();
let container: HTMLElement | null; let disposeObservation: (() => void) | undefined;
function pointer(event: PointerEvent) {
  const pickingTarget = task.task?.kind === 'registration' && reg.pickingTarget;
  const pickingCbct = task.task?.kind === 'cbct-landmark' && cbct.mode === 'landmark';
  if (event.button !== 0 || !(pickingTarget || pickingCbct) || props.imageId !== cbct.imageId || !task.editable || task.busy) return;
  event.preventDefault(); event.stopImmediatePropagation();
  const image = useImageCacheStore().getVtkImageData(props.imageId)!;
  const metadata = useImageCacheStore().getImageMetadata(props.imageId)!;
  const axis = metadata.lpsOrientation[props.axis]; const ijk: [number, number, number] = [0, 0, 0];
  ijk[axis] = useViewSliceStore().getConfig(props.viewId, props.imageId).slice;
  const origin = image.indexToWorld(ijk); ijk[axis] += 1;
  const along = image.indexToWorld(ijk); const normal = along.map((n, i) => n - origin[i]);
  const canvas = container!.querySelector('canvas')!; const rect = canvas.getBoundingClientRect();
  const x = (event.clientX - rect.left) * canvas.width / rect.width;
  const y = (rect.bottom - event.clientY) * canvas.height / rect.height;
  const a = view.renderWindowView.displayToWorld(x, y, 0, view.renderer); const b = view.renderWindowView.displayToWorld(x, y, 1, view.renderer);
  const ray = b.map((n, i) => n - a[i]); const dot = (u: ArrayLike<number>, v: ArrayLike<number>) => u[0]*v[0] + u[1]*v[1] + u[2]*v[2];
  const denominator = dot(normal, ray); if (Math.abs(denominator) < 1e-10) return;
  const t = dot(normal, origin.map((n, i) => n - a[i])) / denominator;
  const point = a.map((n, i) => n + t * ray[i]) as [number, number, number]; const index = image.worldToIndex(point);
  if (index.some((n, i) => n < -0.5 || n > image.getDimensions()[i] - .5)) return;
  if (pickingTarget) reg.pickTarget(point); else cbct.addLandmark(point);
}
onMounted(() => {
  container = view.interactor.getContainer();
  if (!container) return;
  disposeObservation = observeView(`mpr-${props.axis.toLowerCase()}`, view, container);
  container.addEventListener('pointerdown', pointer, true);
});
onBeforeUnmount(() => { disposeObservation?.(); container?.removeEventListener('pointerdown', pointer, true); });
</script>
<template><slot /></template>
